/**
 * GraphQL Hub Repository
 * Handles GraphQL operations for Contacts, Conversations, Deals, Activities
 */

import { graphqlQuery, graphqlMutation, CONTACT_FIELDS } from '../database/graphqlClient';

// =============================================================================
// TYPES
// =============================================================================

export interface Contact {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  linkedin?: string;
  company?: string;
  role?: string;
  notes?: string;
  tags: string[];
  avatarUrl?: string;
  leadStatus?: string;
  leadSource?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatConversation {
  id: string;
  title: string;
  channel?: string;
  unreadCount: number;
  isPinned: boolean;
  isMuted: boolean;
  isArchived: boolean;
  projectId?: string;
  customerId?: string;
  contactId?: string;
  contactName?: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: string;
  isRead: boolean;
  direction?: string;
  attachments: Array<{
    id: string;
    name: string;
    mimeType: string;
    size: number;
    url?: string;
    thumbnailUrl?: string;
  }>;
  replyToId?: string;
  isAiGenerated: boolean;
  status?: string;
}

interface ContactsQueryResult {
  contacts: Array<{
    id: string;
    displayName: string;
    company?: string;
    role?: string;
    notes?: string;
    tags: string[];
    createdAt: string;
    updatedAt: string;
  }>;
}

// =============================================================================
// QUERIES & MUTATIONS
// =============================================================================

const CONVERSATION_FIELDS = `
  id
  title
  channel
  unreadCount
  isPinned
  isMuted
  isArchived
  projectId
  customerId
  contactId
  contactName
  lastMessageAt
  lastMessagePreview
  createdAt
  updatedAt
`;

const MESSAGE_FIELDS = `
  id
  conversationId
  senderId
  senderName
  content
  timestamp
  isRead
  direction
  attachments {
    id
    name
    mimeType
    size
    url
    thumbnailUrl
  }
  replyToId
  isAiGenerated
  status
`;

const QUERIES = {
  contacts: `
    query ListContacts {
      contacts {
        ${CONTACT_FIELDS}
      }
    }
  `,
  chatConversations: `
    query ListChatConversations($channel: String, $archived: Boolean) {
      chatConversations(channel: $channel, archived: $archived) {
        ${CONVERSATION_FIELDS}
      }
    }
  `,
  chatMessages: `
    query ListChatMessages($conversationId: String!) {
      chatMessages(conversationId: $conversationId) {
        ${MESSAGE_FIELDS}
      }
    }
  `,
};

const MUTATIONS = {
  createContact: `
    mutation CreateContact($input: ContactInput!) {
      createContact(input: $input) {
        ${CONTACT_FIELDS}
      }
    }
  `,
  deleteContact: `
    mutation DeleteContact($id: ID!) {
      deleteContact(id: $id)
    }
  `,
};

// =============================================================================
// REPOSITORY
// =============================================================================

export class GraphQLHubRepository {
  async listContacts(): Promise<Contact[]> {
    const result = await graphqlQuery<ContactsQueryResult>(QUERIES.contacts);
    return result.contacts.map(c => ({
      id: c.id,
      displayName: c.displayName,
      email: c.email,
      phone: c.phone,
      whatsapp: c.whatsapp,
      telegram: c.telegram,
      company: c.company,
      role: c.role,
      notes: c.notes,
      tags: c.tags || [],
      isActive: true,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
    }));
  }

  async createContact(data: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>): Promise<Contact> {
    const input = {
      displayName: data.displayName,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      handles: {
        email: data.email || null,
        phone: data.phone || null,
        whatsapp: data.whatsapp || null,
        telegram: data.telegram || null,
        linkedin: data.linkedin || null,
      },
      company: data.company || null,
      role: data.role || null,
      notes: data.notes || null,
      tags: data.tags || [],
      avatarUrl: data.avatarUrl || null,
      leadStatus: data.leadStatus || null,
      leadSource: data.leadSource || null,
    };

    const result = await graphqlMutation<{ createContact: Contact }>(
      MUTATIONS.createContact,
      { input }
    );
    return result.createContact;
  }

  async deleteContact(id: string): Promise<boolean> {
    await graphqlMutation(MUTATIONS.deleteContact, { id });
    return true;
  }

  async listConversations(channel?: string, archived?: boolean): Promise<ChatConversation[]> {
    const result = await graphqlQuery<{ chatConversations: ChatConversation[] }>(
      QUERIES.chatConversations,
      { channel, archived }
    );
    return result.chatConversations || [];
  }

  async listMessages(conversationId: string): Promise<ChatMessage[]> {
    const result = await graphqlQuery<{ chatMessages: ChatMessage[] }>(
      QUERIES.chatMessages,
      { conversationId }
    );
    return result.chatMessages || [];
  }
}

export const graphqlHubRepository = new GraphQLHubRepository();
